import type { DefaultRootState } from "react-redux";
import type { Dispatch } from "redux";
import type { ThunkAction } from "redux-thunk";
import { setToolbarSnack } from "actions/context";
import {
  Breadcrumb,
  type ICreatePosUserAttributes,
} from "interfaces/users/breadcrumb";

import { RequestStatus } from "types/request";
import type { IRoleBreadcrumb, IZone } from "types/user";
import type IPosUser from "types/user/pos_user";

const { getAllRoles, getAllZones, postCreatePosUser } = Breadcrumb;

export type BreadcrumbUserActionTypes =
  | ISetBreadcrumbRequestStatusAction
  | IFetchRolesAction
  | IFetchZonesAction
  | IUpdateLocalUserRecordWithNewPosUserAction;

type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  BreadcrumbUserActionTypes
>;

export enum TypeKeys {
  SET_BREADCRUMB_REQUEST_STATUS_ACTION = "USERS/BREADCRUMB/SET_BREADCRUMB_REQUEST_STATUS_ACTION",
  FETCH_ROLES_ACTION = "USERS/BREADCRUMB/FETCH_ROLES_ACTION",
  FETCH_ZONES_ACTION = "USERS/BREADCRUMB/FETCH_ZONES_ACTION",
  UPDATE_LOCAL_USER_RECORD_WITH_NEW_POS_USER = "USERS/BREADCRUMB/UPDATE_LOCAL_USER_RECORD_WITH_NEW_POS_USER",
}

export enum BreadcrumbRequestKey {
  FetchRoles = "FetchRoles",
  FetchZones = "FetchZones",
  CreatePosUser = "CreatePosUser",
}

export interface ISetBreadcrumbRequestStatusAction {
  type: TypeKeys.SET_BREADCRUMB_REQUEST_STATUS_ACTION;
  requestKey: BreadcrumbRequestKey;
  requestStatus: RequestStatus;
}

export interface IFetchRolesAction {
  type: TypeKeys.FETCH_ROLES_ACTION;
  roles: IRoleBreadcrumb[];
}

export interface IFetchZonesAction {
  type: TypeKeys.FETCH_ZONES_ACTION;
  zones: IZone[];
}

const setBreadcrumbRequestStatus = (
  requestKey: BreadcrumbRequestKey,
  requestStatus: RequestStatus
): ISetBreadcrumbRequestStatusAction => {
  return {
    type: TypeKeys.SET_BREADCRUMB_REQUEST_STATUS_ACTION,
    requestKey,
    requestStatus,
  };
};

export const fetchRoles = (breadcrumbPermalink: string): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setBreadcrumbRequestStatus(
        BreadcrumbRequestKey.FetchRoles,
        RequestStatus.REQUESTING
      )
    );

    return getAllRoles(breadcrumbPermalink)
      .then((roles) => {
        dispatch(
          setBreadcrumbRequestStatus(
            BreadcrumbRequestKey.FetchRoles,
            RequestStatus.SUCCESS
          )
        );

        dispatch({
          type: TypeKeys.FETCH_ROLES_ACTION,
          roles: roles as IRoleBreadcrumb[],
        });
      })
      .catch(() => {
        dispatch(
          setBreadcrumbRequestStatus(
            BreadcrumbRequestKey.FetchRoles,
            RequestStatus.FAILURE
          )
        );
      });
  };
};

export const fetchZones = (breadcrumbPermalink: string): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setBreadcrumbRequestStatus(
        BreadcrumbRequestKey.FetchZones,
        RequestStatus.REQUESTING
      )
    );

    return getAllZones(breadcrumbPermalink)
      .then((zones) => {
        dispatch(
          setBreadcrumbRequestStatus(
            BreadcrumbRequestKey.FetchZones,
            RequestStatus.SUCCESS
          )
        );

        dispatch({
          type: TypeKeys.FETCH_ZONES_ACTION,
          zones,
        });
      })
      .catch(() => {
        dispatch(
          setBreadcrumbRequestStatus(
            BreadcrumbRequestKey.FetchZones,
            RequestStatus.FAILURE
          )
        );
      });
  };
};

export const createPosUser = (
  permalink: string,
  attrs: ICreatePosUserAttributes
): ThunkResult<void> => {
  return (dispatch: Dispatch<any>): Promise<void> => {
    dispatch(
      setBreadcrumbRequestStatus(
        BreadcrumbRequestKey.CreatePosUser,
        RequestStatus.REQUESTING
      )
    );

    return postCreatePosUser(permalink, attrs)
      .then((posUser) => {
        dispatch(
          setBreadcrumbRequestStatus(
            BreadcrumbRequestKey.CreatePosUser,
            RequestStatus.SUCCESS
          )
        );

        dispatch(updateLocalUserRecordWithNewPosUser(posUser, permalink));
        dispatch(
          setToolbarSnack({
            id: "create_pos_user_status",
            type: "success",
            message: `${posUser.firstName} ${
              posUser.lastName
            } has been assigned a POS role.`,
          })
        );
      })
      .catch(() => {
        dispatch(
          setToolbarSnack({
            id: "create_pos_user_status",
            type: "error",
            message: "There was an error assigning this user a POS role.",
          })
        );
      });
  };
};

export interface IUpdateLocalUserRecordWithNewPosUserAction {
  type: TypeKeys.UPDATE_LOCAL_USER_RECORD_WITH_NEW_POS_USER;
  posUser: IPosUser;
  storeKey: string;
}

export function updateLocalUserRecordWithNewPosUser(
  posUser: IPosUser,
  storeKey: string
): IUpdateLocalUserRecordWithNewPosUserAction {
  return {
    type: TypeKeys.UPDATE_LOCAL_USER_RECORD_WITH_NEW_POS_USER,
    posUser,
    storeKey,
  };
}
